/**
 * Site Group Settings V2 — Danger Zone: deactivate this group or reactivate
 * one that was deactivated. Not part of the Save bar's diff because these
 * are destructive lifecycle actions, not edits. Behaviour matches the legacy
 * details tab: red Delete / Reactivate, gated by `add_sitegroup AND
 * delete_sitegroup`.
 *
 * Contains:
 * - SiteGroupDangerZone (default export)
 */

import { Button } from '@mui/material';
import pluralize from 'pluralize';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import { fetchCustomer } from '@sophocles/entities/Customer';
import { RECEIVE_SITE_GROUPS } from '@sophocles/entities/SiteGroup';
import {
  useDeactivateSiteGroupsMutation,
  useReactivateSiteGroupsMutation,
} from '@sophocles/features/entities/siteGroups';
import { setPlant } from '@sophocles/features/sites/slices/selectedPlant/actions';
import { addToast } from '@sophocles/features/toast';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import { genericErrorAction } from '@sophocles/utils';
import { DANGER_ZONE_CONTROL_WIDTH, SettingsDangerZone, SettingsFieldRow } from '@sophocles/views/settings/components';
import BulkDeactivateSiteGroupDialog from '@sophocles/views/siteGroups/components/BulkDeactivateSiteGroupDialog';

export default function SiteGroupDangerZone({ siteGroup, customer, isBillboard, deactivated }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { permissions } = useUser();
  const groupTerm = useSiteGroupTerm();
  const canDelete = permissions.canCreateSiteGroup && permissions.canDeleteSiteGroup;
  const isVirtual = siteGroup.is_virtual ?? false;
  const [busy, setBusy] = React.useState(false);
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  const [deactivateSiteGroups] = useDeactivateSiteGroupsMutation();
  const [reactivateSiteGroups] = useReactivateSiteGroupsMutation();

  const siteGroupId = siteGroup.id ?? Number(params.sitegroup_id);

  // The deactivate dialog re-runs its preflight whenever these change
  // identity, so they are built once rather than inline in the element.
  // They sit above the permission return because hooks run unconditionally.
  const deactivateIds = useMemo(() => [siteGroupId], [siteGroupId]);
  const deactivateRows = useMemo(
    () => [{ id: siteGroupId, name: siteGroup.name, is_virtual: isVirtual }],
    [siteGroupId, siteGroup.name, isVirtual],
  );
  const noFilters = useMemo(() => ({}), []);

  if (!canDelete) return null;
  const customerId = customer?.id ?? params.cust_id;
  const lowerTerm = groupTerm.singular.toLowerCase();

  const handleDeactivateConfirm = ({ radio_action, destination_site_group_id }) => {
    const term = groupTerm.singular;
    deactivateSiteGroups({
      siteGroupIds: [siteGroupId],
      radioAction: radio_action,
      destinationSiteGroupId: destination_site_group_id,
    })
      .unwrap()
      .then(() => {
        dispatch(
          addToast({
            title: t('general.success'),
            message: t('siteGroups.deactivateSuccess', '{{term}} Deactivated', { term }),
            level: 'success',
            dismissible: true,
          }),
        );
        setDeactivateOpen(false);
        dispatch(setPlant(0));
        navigate(`/c/${customerId}/plants`, { replace: true });
        if (isVirtual && customer?.id) {
          dispatch(fetchCustomer(customer.id));
        }
      })
      .catch(() => {
        dispatch(
          addToast({
            title: t('general.error'),
            message: t('siteGroups.deactivateError', 'Failed to deactivate {{term}}. Please try again.', { term }),
            level: 'error',
            dismissible: true,
          }),
        );
      });
  };

  const handleReactivate = () => {
    if (busy) return;
    setBusy(true);
    reactivateSiteGroups({ siteGroupIds: [siteGroupId] })
      .unwrap()
      .then(() => {
        dispatch(
          addToast({
            title: 'Success!',
            message: `${isBillboard ? pluralize('Plant', 1) : 'Site ' + pluralize('Group', 1)} Reactivated`,
            level: 'success',
            dismissible: true,
            autoDismiss: 5,
          }),
        );
        navigate(`/c/${customerId}/plants`, { replace: true });
      })
      .catch(error => {
        dispatch(genericErrorAction(error, RECEIVE_SITE_GROUPS));
        setBusy(false);
      });
  };

  return (
    <>
      <SettingsDangerZone
        description={t('siteGroupSettings.v2.dangerZoneDescription', 'Deactivate or reactivate this {{groupTerm}}.', {
          groupTerm: lowerTerm,
        })}
      >
        <SettingsFieldRow
          label={
            deactivated
              ? t('siteGroupSettings.v2.reactivate', 'Reactivate')
              : t('siteGroupSettings.v2.deactivate', 'Deactivate')
          }
          description={
            deactivated
              ? t('siteGroupSettings.v2.reactivateDescription', 'Restore this {{groupTerm}} and its sites.', {
                  groupTerm: lowerTerm,
                })
              : t(
                  'siteGroupSettings.v2.deactivateDescription',
                  'Deactivate this {{groupTerm}}. Hidden from default lists; can be restored later.',
                  { groupTerm: lowerTerm },
                )
          }
          controlWidth={DANGER_ZONE_CONTROL_WIDTH}
        >
          {deactivated ? (
            <Button variant="outlined" color="success" disabled={busy} onClick={handleReactivate}>
              {isBillboard ? t('siteGroups.reactivatePlant') : t('siteGroups.reactivateSiteGroup')}
            </Button>
          ) : (
            <Button variant="contained" color="error" disabled={busy} onClick={() => setDeactivateOpen(true)}>
              {isBillboard ? t('siteGroups.deletePlant') : t('siteGroups.deleteSiteGroup')}
            </Button>
          )}
        </SettingsFieldRow>
      </SettingsDangerZone>
      <BulkDeactivateSiteGroupDialog
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={handleDeactivateConfirm}
        siteGroupIds={deactivateIds}
        siteGroupRows={deactivateRows}
        selectAll={false}
        filters={noFilters}
        customerId={customerId}
        isBillboard={isBillboard}
      />
    </>
  );
}
